export const useAvatarRegistry = () => {
    // Shared state across components
    const avatars = useState<Record<string, string>>('avatarRegistry', () => ({}))
    const { socket: gameSocket } = useGameSocket()

    const isRegistered = useState<boolean>('avatarRegistryRegistered', () => false)

    // Helper to get avatar or request it if missing
    const getAvatar = (playerId: string) => {
        if (!playerId) return undefined

        // Auto-register listeners if socket exists and not already done
        if (gameSocket.value && !isRegistered.value) registerSocketListeners()

        if (typeof playerId !== 'string') {
            // console.warn('getAvatar called with non-string id:', playerId)
            return undefined
        }

        // If we have it, return it
        if (avatars.value[playerId]) {
            return avatars.value[playerId]
        }

        // We don't have it, but we can't request it inside a template render function easily
        // without triggering infinite loops or too many requests.
        // Instead, components should watch their player lists and call `ensureAvatars`
        return undefined
    }

    const ensureAvatars = (playerIds: string[]) => {
        const missing = playerIds.filter(pid => !avatars.value[pid])
        if (missing.length > 0 && gameSocket.value) {
            // Debounce or batch could be added here, but for now direct request
            gameSocket.value.emit('request_avatars', missing)
        }
    }

    const setAvatar = (playerId: string, avatar: string) => {
        if (!playerId || !avatar) return
        avatars.value = {
            ...avatars.value,
            [playerId]: avatar,
        }
    }

    const registerSocketListeners = () => {
        if (!gameSocket.value || isRegistered.value) return

        gameSocket.value.on('avatars_received', (data: Record<string, string>) => {
            // Use spread for reactivity
            avatars.value = { ...avatars.value, ...data }
        })
        gameSocket.value.on('avatar_updated', (data: { playerId: string }) => {
            if (!data?.playerId || !gameSocket.value) return
            gameSocket.value.emit('request_avatars', [data.playerId])
        })
        isRegistered.value = true
    }

    // Auto-register as soon as socket is available
    watch(gameSocket, (s) => {
        if (s && !isRegistered.value) registerSocketListeners()
    }, { immediate: true })

    return {
        avatars,
        getAvatar,
        ensureAvatars,
        setAvatar,
        registerSocketListeners
    }
}
